import { useCallback, useContext, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import useFlightFareLabels from 'hooks/Flights/useFlightFareLabels'
import { getFlightDealDuration } from 'lib/flights/flightUtils'
import Caption from 'LuxKit/Typography/Caption'
import BodyText from 'LuxKit/Typography/BodyText'
import LineArrowRightIcon from 'LuxKit/Icons/line/LineArrowRightIcon'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LineExchangeAltIcon from 'LuxKit/Icons/line/LineExchangeAltIcon'
import Image from 'components/Common/Image'
import LineAwardIcon from 'LuxKit/Icons/line/LineAwardIcon'
import PromotionLabel from 'LuxKit/Label/PromotionLabel'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { INTL_DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import styled from 'styled-components'
import { rem } from 'polished'
import TextLink from 'LuxKit/TextLink'
import Clickable from 'components/Common/Clickable'
import { EventDataKey } from 'home/pages/HomePage/useHomepageAnalytics'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import { useInView } from 'react-intersection-observer'
import { OFFER_TRACKING_IN_VIEW_THRESHOLD } from 'constants/offerList'
import FlightDealPoints from './FlightDealPoints'
import useIsOpenNewTabOfferClickEnabled from 'hooks/Offers/useIsOpenNewTabOfferClickEnabled'

const DealContainer = styled(Group)`
  padding: ${rem(16)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
`

const WrappingPromotionLabel = styled(PromotionLabel)`
  padding-block: ${rem(2)};
  text-align: center;
`

const NoWrap = styled.span`
  white-space: nowrap;
`

const Separator = styled.div`
  height: 1px;
  width: 100%;
  background-color: ${props => props.theme.palette.neutral.surface.lightForeground};
  margin-top: ${rem(16)};
`

const ContentContainer = styled(VerticalSpacer)`
  flex: 1;
`

interface Props {
  flightDeal: App.FlightDeal
  position?: number
}

function FlightDealTile(props: Props) {
  const { flightDeal, position } = props
  const intl = useIntl()
  const { fareTypes, fareCabins } = useFlightFareLabels()
  const fareTypeLabel = fareTypes.find(type => type.value === flightDeal.fareType)?.label
  const fareClassLabel = fareCabins.find(type => type.value === flightDeal.fareClass)?.label
  const travelDates = useMemo(() => getFlightDealDuration(flightDeal.travelPeriods), [flightDeal.travelPeriods])
  const isOnewayFlight = flightDeal.fareType === 'oneWay'
  const dateFormatter = useIntlDateFormatter()
  const dispatchOfferListEvent = useContext(OfferListEventsContext)
  const isOpenNewTabOfferClickEnabled = useIsOpenNewTabOfferClickEnabled()

  const oneWayFlightLabel = intl.formatMessage({
    id: 'aRHFsNUJ4p2hPoCiJJqF',
    defaultMessage: 'One way flight',
    description: 'Form field label for one-way flight type in flight deal tile',
  })
  const returnFlightsLabel = intl.formatMessage({
    id: '2Q_-G0NikQndLo7cUXU7',
    defaultMessage: 'Return flights',
    description: 'Form field label for return flight type in flight deal tile',
  })

  const [inViewRef, hasBeenInView] = useInView({
    triggerOnce: true,
    threshold: OFFER_TRACKING_IN_VIEW_THRESHOLD,
  })

  useEffect(() => {
    if (hasBeenInView && position !== undefined) {
      dispatchOfferListEvent({
        type: OfferListEvents.productImpression,
        offer: flightDeal,
        position,
        key: EventDataKey.FlightImpression,
      })
    }
  }, [hasBeenInView, flightDeal, position, dispatchOfferListEvent])

  const onClick = useCallback(() => {
    if (position !== undefined) {
      dispatchOfferListEvent({
        type: OfferListEvents.productClick,
        offer: flightDeal,
        position,
        key: EventDataKey.FlightClick,
      })
    }
  }, [position, flightDeal, dispatchOfferListEvent])

  return (
    <Clickable
      to={`/flight/deal/${flightDeal.id}`}
      target={isOpenNewTabOfferClickEnabled ? '_blank' : undefined}
      ref={inViewRef}
      onClick={onClick}
    >
      <DealContainer direction="vertical" gap={12} verticalAlign="space-between" fullHeight>
        <ContentContainer gap={20}>
          <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center" gap={16}>
            <Image
              image={flightDeal.carrier.logo}
              width={100}
              alt={intl.formatMessage(
                {
                  id: 'dEm8vCGBtEDJVX4iKDtL',
                  defaultMessage: 'Logo of {carrierName}',
                  description: 'Alt text for airline carrier logo on flight deal tile',
                },
                { carrierName: flightDeal.carrier.name },
              )}
            />
            <WrappingPromotionLabel variant="default" icon={<LineAwardIcon />}>
              {intl.formatMessage(
                {
                  id: 'bWGhWnMtaLBCNBIy74JN',
                  defaultMessage: 'Sale ends {endDate}',
                  description: 'Promotion label showing sale end date on flight deal tile',
                },
                { endDate: <NoWrap>{dateFormatter(flightDeal.salesEndDate, INTL_DMY_CASUAL_SHORT_FORMAT)}</NoWrap> },
              )}
            </WrappingPromotionLabel>
          </Group>

          <Group direction="horizontal" horizontalAlign="space-between" gap={16}>
            <Group direction="horizontal" gap={4} verticalAlign="end">
              <VerticalSpacer gap={2}>
                <Caption variant="small" colour="light-foreground-alt" weight="bold" format="uppercase">
                  {flightDeal.originAirportCode}
                </Caption>
                <BodyText variant="large" weight="bold">{flightDeal.originName}</BodyText>
              </VerticalSpacer>
              {isOnewayFlight ? <LineArrowRightIcon /> : <LineExchangeAltIcon />}
              <VerticalSpacer gap={2}>
                <Caption variant="small" colour="light-foreground-alt" weight="bold" format="uppercase">
                  {flightDeal.destinationAirportCode}
                </Caption>
                <BodyText variant="large" weight="bold">{flightDeal.destinationName}</BodyText>
              </VerticalSpacer>
            </Group>
          </Group>

          <VerticalSpacer gap={2}>
            <BodyText variant="small" colour="light-foreground-alt">
              {intl.formatMessage(
                {
                  id: 'MlDzqWy_oTisGKfbZlmT',
                  defaultMessage: '{flightType} · {fareClass}',
                  description: 'Flight type and fare class summary in flight deal tile, e.g. "Return flights · Economy"',
                },
                { flightType: isOnewayFlight ? oneWayFlightLabel : returnFlightsLabel, fareClass: fareClassLabel },
              )}
            </BodyText>
            <BodyText variant="small" colour="light-foreground-alt">
              {intl.formatMessage(
                {
                  id: '7q_02o9yjes1EjrzeDG4',
                  defaultMessage: 'Travel between {travelDates}',
                  description: 'Travel date range for flight deal tile in the flight search or booking flow',
                },
                { travelDates },
              )}
            </BodyText>
          </VerticalSpacer>

          <FlightDealPoints flightDeal={flightDeal} />
        </ContentContainer>

        <Separator />

        <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
          <div>
            <PriceRowPriceCaption>
              {intl.formatMessage(
                {
                  id: '52xgbUnnZLiXoPGk9g9b',
                  defaultMessage: '{fareType} from',
                  description: 'Price caption showing fare type for flight deal tile',
                },
                { fareType: fareTypeLabel },
              )}
            </PriceRowPriceCaption>
            <PriceRowPrice
              size="L"
              price={flightDeal.perAdultPrice}
              saleUnit="adult"
            />
          </div>
          <TextLink
            size="caption"
            weight="regular"
          >
            {intl.formatMessage({
              id: 'xliQVprWEbeBHC5CsxcL',
              defaultMessage: 'View flights',
              description: 'Link text to view flights in flight deal tile component',
            })}
          </TextLink>
        </Group>
      </DealContainer>
    </Clickable>
  )
}

export default FlightDealTile
